package ru.it_mozaika.sender.js.application.frames.campaigns

import io.ktor.client.call.*
import js.core.jso
import kotlinext.js.assign
import mui.material.*
import mui.material.Size
import mui.system.responsive
import mui.system.sx
import react.*
import react.dom.aria.ariaLabel
import react.router.dom.useSearchParams
import react.router.useNavigate
import ru.it_mozaika.components.AvatarButton
import ru.it_mozaika.components.searchField
import ru.it_mozaika.components.commonTableSkeletonFrame
import ru.it_mozaika.components.useSnackbar
import ru.it_mozaika.sender.common.models.EmailCampaignData
import ru.it_mozaika.sender.common.models.EmailCampaignStatus
import ru.it_mozaika.sender.common.models.EmailCampaignTitle
import ru.it_mozaika.sender.js.application.Http
import ru.it_mozaika.utils.checkResponseError
import ru.it_mozaika.sender.js.application.components.StoresSelect
import ru.it_mozaika.sender.js.application.components.campaigns.EmailCampaignStartDialog
import ru.it_mozaika.sender.js.application.context.useBackdrop
import ru.it_mozaika.sender.js.application.context.useStore
import ru.it_mozaika.sender.js.application.frames.MainCard
import ru.it_mozaika.sender.js.application.toLocalString
import tanstack.react.table.useReactTable
import tanstack.table.core.ColumnDef
import tanstack.table.core.ColumnDefTemplate
import tanstack.table.core.StringOrTemplateHeader
import tanstack.table.core.getCoreRowModel
import web.cssom.*
import web.window.WindowTarget

val CampaignsFrame = VFC {
    val (params, setParams) = useSearchParams()
    val backdrop = useBackdrop()
    val showNotification = useSnackbar().enqueueSnackbar
    var campaigns by useState(listOf<EmailCampaignTitle>())
    val navigate = useNavigate()
    var size by useState(0)
    var page by useState(params["page"]?.toIntOrNull() ?: 0)
    var rows by useState(params["rows"]?.toIntOrNull() ?: 10)
    val store = useStore().currentStoreId
    var search by useState(params["search"].orEmpty())
    val tableColumns = listOf("id" to "ID", "name" to "Название", "status" to "Статус", "actions" to "Действия")
    var campaignToSend by useState<EmailCampaignData>()
    var campaignToPause by useState<EmailCampaignTitle>()
    var campaignToDelete by useState<EmailCampaignTitle>()

    fun loadCampaignsSize() {
        loadCampaignsSize(
            store.orEmpty(),
            search,
            { showNotification(it, jso { variant = AlertColor.error }) }
        ) {
            size = it
        }
    }

    fun loadCampaigns() {
        loadCampaigns(
            store.orEmpty(),
            page,
            rows,
            search,
            { showNotification(it, jso { variant = AlertColor.error }) }) {
            campaigns = it
        }
    }
    useEffect(store, search) { loadCampaignsSize() }
    useEffect(rows, page, search, store) { loadCampaigns() }

    EmailCampaignStartDialog {
        campaign = campaignToSend
        this.onClose = {
            campaignToSend = null
            if (it) {
                loadCampaignsSize()
                loadCampaigns()
            }
        }
    }
    val table = useReactTable<EmailCampaignTitle>(
        options = jso {
            this.columns = useMemo(tableColumns) {
                tableColumns.map { (column, title) ->
                    jso<ColumnDef<EmailCampaignTitle, Any?>> {
                        accessorFn = { item, _ ->
                            when (column) {
                                "id" -> item.id
                                "name" -> item.name
                                "status" -> {
                                    when (item.status) {
                                        EmailCampaignStatus.Template -> "Черновик"
                                        EmailCampaignStatus.Delayed -> "Отложена до ${
                                            item.startDate?.toLocalString().orEmpty()
                                        }"

                                        EmailCampaignStatus.Preparation -> "Подготовка к отправке"
                                        EmailCampaignStatus.Ready -> "Готова к отправке"
                                        EmailCampaignStatus.Process -> "Отправляется"
                                        EmailCampaignStatus.Complete -> "Завершена ${
                                            item.startDate?.toLocalString().orEmpty()
                                        }"

                                        EmailCampaignStatus.Pause -> "Приостановлена"
                                    }
                                }

                                else -> ""
                            }
                        }
                        cell = ColumnDefTemplate { context ->
                            if (context.column.id == "actions") {
                                Stack.create {
                                    direction = responsive(StackDirection.row)
                                    spacing = responsive(2)
                                    Tooltip {
                                        this.title = ReactNode("Скопировать")
                                        this.placement = TooltipPlacement.bottom
                                        IconButton {
                                            onClick = { navigate("/campaigns/new?id=${context.row.original.id}") }
                                            mui.icons.material.ContentCopy()
                                        }
                                    }
                                    when (context.row.original.status) {
                                        EmailCampaignStatus.Template -> {
                                            Tooltip {
                                                this.title = ReactNode("Запустить")
                                                this.placement = TooltipPlacement.bottom
                                                IconButton {
                                                    color = IconButtonColor.success
                                                    mui.icons.material.PlayCircleOutline()
                                                    onClick = {
                                                        backdrop.show()
                                                        Http.get("/campaigns/${context.row.original.id}") { response ->
                                                            backdrop.hide()
                                                            response.checkResponseError()?.let { error ->
                                                                showNotification(
                                                                    error,
                                                                    jso { variant = AlertColor.error })
                                                                navigate("/campaigns")
                                                                return@get
                                                            }
                                                            campaignToSend = response.body<EmailCampaignData>()
                                                        }
                                                    }
                                                }
                                            }
                                            Tooltip {
                                                this.title = ReactNode("Удалить")
                                                this.placement = TooltipPlacement.bottom
                                                IconButton {
                                                    color = IconButtonColor.error
                                                    onClick = { campaignToDelete = context.row.original }
                                                    mui.icons.material.Delete()
                                                }
                                            }
                                        }

                                        EmailCampaignStatus.Preparation,
                                        EmailCampaignStatus.Ready -> {
                                        }

                                        EmailCampaignStatus.Delayed,
                                        EmailCampaignStatus.Process -> {
                                            Tooltip {
                                                this.title = ReactNode("Пауза")
                                                this.placement = TooltipPlacement.bottom
                                                IconButton {
                                                    onClick = { campaignToPause = context.row.original }
                                                    color = IconButtonColor.warning
                                                    mui.icons.material.Pause()
                                                }
                                            }
                                        }

                                        EmailCampaignStatus.Pause -> {
                                            Tooltip {
                                                this.title = ReactNode("Запустить")
                                                this.placement = TooltipPlacement.bottom
                                                IconButton {
                                                    color = IconButtonColor.success
                                                    mui.icons.material.PlayCircleOutline()
                                                    onClick = {
                                                        backdrop.show()
                                                        Http.get("/campaigns/${context.row.original.id}") { response ->
                                                            backdrop.hide()
                                                            response.checkResponseError()?.let { error ->
                                                                showNotification(
                                                                    error,
                                                                    jso { variant = AlertColor.error })
                                                                navigate("/campaigns")
                                                                return@get
                                                            }

                                                            campaignToSend = response.body<EmailCampaignData>()
                                                        }
                                                    }
                                                }
                                            }
                                        }

                                        EmailCampaignStatus.Complete -> {}
                                    }
                                }
                            } else {
                                ReactNode(context.getValue().toString())
                            }
                        }
                        id = column
                        header = StringOrTemplateHeader(title)
                    }
                }
            }.toTypedArray()
            this.data = useMemo(campaigns) { campaigns.toTypedArray() }
            this.state = jso {
            }
            this.onColumnVisibilityChange = {
            }
            this.onColumnOrderChange = {
            }
            this.getCoreRowModel = getCoreRowModel()
            this.debugTable = true
            this.debugHeaders = true
            this.debugColumns = true
        }
    )
    MainCard {
        title = "Массовые рассылки"
        actions = Stack.create {
            this.direction = responsive(StackDirection.row)
            this.divider = Divider.create {
                orientation = Orientation.vertical
            }
            asDynamic().spacing = 2
            sx {
                this.width = 100.pct
                border = 2.px
                this.alignItems = AlignItems.center
                this.justifyContent = JustifyContent.flexStart
            }
            AvatarButton {
                tooltipTitle = "Создать"
                mui.icons.material.AddCircleOutlineSharp()
                asDynamic().component = rawForwardRef { p, r ->
                    createElement(
                        Link,
                        assign(p.unsafeCast<LinkProps>()) {
                            ref = r
                            href = "/campaigns/new"
                            target = WindowTarget._self
                        },
                        p.unsafeCast<PropsWithChildren>().children
                    )
                }
                onClick = {
                    it.preventDefault()
                    it.stopPropagation()
                    navigate("/campaigns/new")
                }
            }
        }
        Stack {
            direction = responsive(StackDirection.row)
            spacing = responsive(2)
            sx {
                this.width = 100.pct
                border = 2.px
                this.alignItems = AlignItems.center
                this.justifyContent = JustifyContent.flexStart
            }
            searchField {
                this.onSearchQueryChange = {
                    search = it
                }
            }
            StoresSelect()
        }
        commonTableSkeletonFrame {
            showCheckBox = false
            this.toolbarActions = Stack.create {
                direction = responsive(StackDirection.row)
                ariaLabel = "campaigns-table-button-group"
                spacing = responsive(2)
            }
            this.table = table
            this.onPropertySelect = { property ->
            }
            this.columnId = "id"
            this.label = "campaigns"
            this.onDoubleClickHandler = {
                navigate("/campaigns/$it")
            }
            this.onSelectionChanged = { newSelected ->
            }
        }
        TablePagination {
            this.page = page
            this.count = size
            this.rowsPerPage = rows
            onPageChange = { _, newPage ->
                page = newPage.toInt()
            }
            onRowsPerPageChange = { event ->
                rows = event.target.asDynamic().value as Int
                page = 0
            }
        }
    }

    PauseConfirmationDialog {
        campaign = campaignToPause
        onClose = {
            campaignToPause = null
            if (it) {
                loadCampaignsSize()
                loadCampaigns()
            }
        }
    }

    DeleteConfirmationDialog {
        campaign = campaignToDelete
        onClose = {
            campaignToDelete = null
            if (it) {
                loadCampaignsSize()
                loadCampaigns()
            }
        }
    }
}

external interface CampaignDialogProps : Props {
    var campaign: EmailCampaignTitle?
    var onClose: (Boolean) -> Unit
}
private val PauseConfirmationDialog = FC<CampaignDialogProps> { props ->
    val backdrop = useBackdrop()
    val showNotification = useSnackbar().enqueueSnackbar
    Dialog {
        open = null != props.campaign
        onClose = { _, _ -> props.onClose(false) }
        DialogTitle { +"Остановить рассылку?" }
        DialogContent {
            DialogContentText { +"Вы уверены, что хотите остановить массовую рассылку ${props.campaign?.name.orEmpty()}?" }
        }
        DialogActions {
            Button {
                onClick = { props.onClose(false) }
                autoFocus = true
                size = Size.small
                +"Назад"
            }
            Button {
                onClick = {
                    backdrop.show()
                    Http.get("/campaigns/${props.campaign?.id}/pause") { response ->
                        backdrop.hide()
                        props.onClose(true)
                        response.checkResponseError()?.let {
                            showNotification(it, jso { variant = mui.material.AlertColor.error })
                        }
                    }
                }
                variant = ButtonVariant.contained
                color = ButtonColor.error
                size = Size.small
                +"Остановить"
            }
        }
    }
}

private val DeleteConfirmationDialog = FC<CampaignDialogProps> { props ->
    val showNotification = useSnackbar().enqueueSnackbar
    val backdrop = useBackdrop()
    Dialog {
        open = null != props.campaign
        onClose = { _, _ -> props.onClose(false) }
        DialogTitle { +"Удалить рассылку?" }
        DialogContent {
            DialogContentText { +"Вы уверены, что хотите удалить массовую рассылку ${props.campaign?.name.orEmpty()}? Данное действие невозможно отменить" }
        }
        DialogActions {
            Button {
                onClick = { props.onClose(false) }
                autoFocus = true
                size = Size.small
                +"Назад"
            }
            Button {
                onClick = {
                    backdrop.show()
                    Http.delete("/campaigns/${props.campaign?.id}") { response ->
                        backdrop.hide()
                        props.onClose(true)
                        response.checkResponseError()?.let {
                            showNotification(it, jso { variant = AlertColor.error })
                        }
                    }
                }
                variant = ButtonVariant.contained
                color = ButtonColor.error
                size = Size.small
                startIcon = mui.icons.material.Delete.create()
                +"Удалить"
            }
        }
    }
}



fun loadCampaignsSize(store: String, search: String, onError: (String) -> Unit, callback: (Int) -> Unit) {
    Http.get("/campaigns/size", mapOf("search" to search, "store" to store.orEmpty())) { response ->
        response.checkResponseError()?.let {
            onError(it)
            return@get
        }
        callback(response.body())
    }
}
fun loadCampaigns(store: String, page: Int, rows: Int, search: String, onError: (String) -> Unit, callback: (List<EmailCampaignTitle>) -> Unit) {
    Http.get(
        "/campaigns",
        mapOf(
            "store" to store.orEmpty(),
            "page" to (page + 1).toString(),
            "rows" to rows.toString(),
            "search" to search
        )
    ) { response ->
        response.checkResponseError()?.let {
            onError(it)
            return@get
        }
        callback(response.body())
    }
}